import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { STUDENT } from '../../constants/userRoles'
import {
  Typography,
  Modal,
  Input,
  List,
  Collapse,
  Button,
  Form,
  Select
} from 'antd'
import {
  CaretRightOutlined,
  ExclamationCircleOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { FlexSectionHeader } from '../style'
import CourseCard from '../../components/CourseCard'
import Spinner from '../../components/Spinner'
import {
  createCourse,
  getAllCourses,
  deleteCourse,
  enroll,
  unEnroll
} from '../../reducers/courseReducer'
import { useNavigate } from 'react-router-dom'
import { getAllCategory } from '../../reducers/courseCategoryReducer'

const { Title, Text } = Typography
const { confirm } = Modal
const { Option } = Select

const Courses = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getAllCourses('published'))
  }, [dispatch])

  useEffect(() => {
    dispatch(getAllCategory())
  }, [])

  const user = useSelector((state) => state.auth.user)
  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)

  const courses = useSelector((state) =>
    Array.isArray(state?.courses?.data)
      ? state?.courses?.data.filter((course) => course?.status !== 'archived')
      : []
  )
  const loading = useSelector((state) => state?.courses?.loading)
  const category = useSelector((state) => state?.category?.data)
  const navigate = useNavigate()

  const [filter, setFilter] = useState('')
  const filteredCourses = courses?.filter((course) => {
    const courseName = course?.name || ''
    const searchFilter = filter || ''
    return courseName.toLowerCase().includes(searchFilter.toLowerCase())
  })

  const publicCourses = filteredCourses?.filter((course) => !course.enrolled)
  const enrolledCourses = filteredCourses?.filter((course) => course.enrolled)

  const handleCancel = () => {
    setModalVisible(false)
  }

  const addCourse = async (values) => {
    try {
      dispatch(createCourse(values))
      setModalVisible(false)
      form.resetFields()
    } catch (err) {
      console.error('Create/enroll failed:', err)
    }
  }

  const removeCourse = (courseId) => {
    confirm({
      title: 'Are you sure you want to delete this course?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        dispatch(deleteCourse(courseId))
      },
      onCancel() {
        return
      }
    })
  }

  const reviewCourse = (courseId) => {
    navigate(`/app/course/${courseId}/modules`)
  }

  const handleEnroll = (courseId, userId) => {
    confirm({
      title: 'Do you want to enroll in this course?',
      icon: <ExclamationCircleOutlined />,
      content: 'You have to enroll in the course to view its content',
      onOk() {
        dispatch(enroll(courseId, userId))
      },
      onCancel() {
        return
      }
    })
  }

  const handleUnenroll = (courseId, userId) => {
    confirm({
      title: 'Do you want to unenroll in this course?',
      icon: <ExclamationCircleOutlined />,
      content: 'You will lose access to this course after unenrolling.',
      onOk() {
        dispatch(unEnroll(courseId, userId))
      },
      onCancel() {
        return
      }
    })
  }

  const handleSearch = (value) => {
    setFilter(value)
  }

  const confirmEnrolled = function (courseId, userId) {
    handleEnroll(courseId, userId)
  }

  const handleCourseCardClick = (courseId, userId, userEnrolled) => {
    if (userEnrolled) navigate(`/app/course/${courseId}/modules`)
    else confirmEnrolled(courseId, userId)
  }

  if (loading) return <Spinner size="large" />

  return (
    <React.Fragment>
      <FlexSectionHeader>
        <Title level={3}>All Courses</Title>

        <div style={{ display: 'flex', gap: '8px' }}>
          <Input.Search
            allowClear
            value={filter}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Enter course name"
            size="large"
            style={{ width: '300px', alignSelf: 'center' }}
          />
          {user && user.role !== STUDENT && (
            <Button
              onClick={() => setModalVisible(true)}
              type="dashed"
              shape="round"
              style={{ height: '40px' }}
              icon={<PlusOutlined />}
            >
              Add Course
            </Button>
          )}
        </div>
      </FlexSectionHeader>

      <Modal
        title="Add New Course"
        visible={modalVisible}
        onOk={form.submit}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={form.submit}>
            Submit
          </Button>
        ]}
      >
        <Form
          name="add Course"
          form={form}
          onFinish={addCourse}
          requiredMark={false}
          layout="vertical"
        >
          <Form.Item
            name="courseName"
            rules={[
              {
                required: true,
                message: 'Please enter the course name'
              },
              {
                max: 50,
                message: 'Course name cannot exceed 50 characters'
              }
            ]}
          >
            <Input placeholder="Course Name" />
          </Form.Item>

          <Form.Item
            name="categoryId"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select
              placeholder="Select a category"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {category?.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.categoryName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description">
            <Input.TextArea placeholder="Description (Optional)" allowClear />
          </Form.Item>

          <Form.Item name="image">
            <Input placeholder="Cover Image (Image Url Optional)" />
          </Form.Item>
        </Form>
      </Modal>

      <div style={{ marginTop: '8px' }}>
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          defaultActiveKey={['1', '2']}
          ghost
        >
          <Collapse.Panel header={<Text strong>Enrolled Courses</Text>} key="1">
            <List
              grid={{
                gutter: 24,
                column: 3,
                xs: 1,
                sm: 2,
                xxl: 5
              }}
              dataSource={enrolledCourses}
              renderItem={(course) => (
                <List.Item>
                  <CourseCard
                    course={course}
                    removeCourse={() => removeCourse(course.id)}
                    reviewCourse={() => reviewCourse(course.id)}
                    handleEnroll={() => handleEnroll(course.id, user._id)}
                    handleUnenroll={() => handleUnenroll(course.id, user._id)}
                    onClick={() =>
                      handleCourseCardClick(
                        course.id,
                        user._id,
                        course.enrolled
                      )
                    }
                  />
                </List.Item>
              )}
            />
          </Collapse.Panel>

          <Collapse.Panel header={<Text strong>Browse Courses</Text>} key="2">
            <List
              grid={{
                gutter: 24,
                column: 3,
                xs: 1,
                sm: 2,
                xxl: 5
              }}
              dataSource={publicCourses}
              renderItem={(course) => (
                <List.Item>
                  <CourseCard
                    course={course}
                    removeCourse={() => removeCourse(course.id)}
                    reviewCourse={() => reviewCourse(course.id)}
                    handleEnroll={() => handleEnroll(course.id, user._id)}
                    handleUnenroll={() => handleUnenroll(course.id, user._id)}
                    onClick={() =>
                      handleCourseCardClick(
                        course.id,
                        user._id,
                        course.enrolled
                      )
                    }
                  />
                </List.Item>
              )}
            />
          </Collapse.Panel>
        </Collapse>
      </div>
    </React.Fragment>
  )
}

export default Courses
